import React, { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-8 text-center">
            <AlertCircle size={28} className="text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Invalid Link</h1>
            <p className="text-slate-400 text-sm mb-6">This password reset link is invalid or missing.</p>
            <Link to="/admin/forgot-password" className="text-amber-400 hover:text-amber-300 text-sm">
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-8 text-center">
            <div className="w-14 h-14 !rounded-[10px] bg-emerald-400/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={28} className="text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="reset-success-title">Password Reset</h1>
            <p className="text-slate-400 text-sm mb-6">
              Your password has been updated successfully. You can now log in with your new password.
            </p>
            <Link
              to="/admin"
              className="inline-flex items-center justify-center w-full h-11 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold !rounded-[10px] transition-colors"
              data-testid="go-to-login-button"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-8 text-sm"
        >
          <ArrowLeft size={16} />
          Back to login
        </Link>

        <div className="bg-slate-900/80 border border-slate-800 !rounded-[10px] p-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_project-flow-beta/artifacts/dh5z86or_PM%20Flow.svg"
              alt="PM Flow Logo"
              className="h-10 w-auto"
            />
          </div>

          <h1 className="text-2xl font-bold text-white text-center mb-2" data-testid="reset-password-title">
            Set New Password
          </h1>
          <p className="text-slate-400 text-center text-sm mb-8">
            Enter your new password below.
          </p>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 !rounded-[10px] p-3 mb-6 text-red-400 text-sm" data-testid="reset-error">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-slate-300 mb-2">New Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
                  data-testid="reset-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 !rounded-[10px]"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold !rounded-[10px] h-11"
              data-testid="reset-submit-button"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
